import { Component, Input, OnInit } from "@angular/core";
import { AdditionalIncomeService } from "app/modules/statement/services/additional-income.service";
import { SelectsData, SelectsDataInterface, Statement } from "app/statements";
import { Store } from "@ngrx/store";
import { getSelectsData, State } from "app/reducers";
import { SubscribingComponent } from "app/common";
import { takeUntil } from "rxjs";
import { VerificationCodes } from "../../../../../statements/verification-codes";
import { VatDeclaration } from "../../../models/additional-income/vat-declaration.model";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmCancelDialogComponent } from "../../../../../shared/components/confirm-cancel-dialog/confirm-cancel-dialog.component";

export enum declarationTypes {
  BUSINESS = "business",
  VAT_INCOME = "vat_income"
}

@Component({
  selector: "app-additional-income-form-vat",
  templateUrl: "./additional-income-form-vat.component.html",
  styleUrls: ["./additional-income-form-vat.component.scss"],
  standalone: false
})
export class AdditionalIncomeFormVatComponent extends SubscribingComponent implements OnInit {
  @Input() vatDeclaration: VatDeclaration;
  @Input() index = 0;
  @Input() statement: Statement;
  @Input() isBusiness: boolean = false;
  public dataSelects: SelectsDataInterface = new SelectsData();
  public editIndex: number = 0;
  public calculatedVat: Record<string, number> = {};
  public vatPayOrRefund: number | string = null;
  public totalVat: number | string = null;
  public taxationTypes: object[] = [
    { id: "agreed_fees", name: "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.TAXATION_TYPE.AGREED" },
    { id: "fees_received", name: "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.TAXATION_TYPE.RECEIVED" }
  ];
  public incomeSumWarning: boolean = false;
  public incomeSumError: boolean = false;
  public lastYearIncomeError: boolean = false;

  constructor(
    public additionalIncomeService: AdditionalIncomeService,
    private store: Store<State>,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(getSelectsData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((selectsData: SelectsDataInterface) => {
        this.dataSelects = selectsData;
      });

    setTimeout(() => {
      if (this.totalVat !== null && this.vatDeclaration.advance_payments !== null) {
        this.recalcPaymentOrRefund();
      }
    });
  }

  public isNumber(value: unknown): boolean {
    return value !== null && value !== "" && !isNaN(Number(value));
  }

  public validateIncomeSum(value: number) {
    const threshold = AdditionalIncomeFormVatComponent.getBusinessIncomeThreshold(this.statementYear);
    this.incomeSumWarning = value > threshold && this.statementYear < 2025;
    this.incomeSumError = value > threshold && this.statementYear >= 2025;

    if (this.incomeSumError) this.vatDeclaration.premium_selected = false;
  }

  private static getBusinessIncomeThreshold(fullYear: number): number {
    return fullYear >= 2025 ? 100000 : 50000;
  }

  public validateLastYearIncome(value: number) {
    this.lastYearIncomeError =
      (value > 25000 && this.statementYear >= 2025) || (value > 22000 && this.statementYear < 2025);

    if (this.lastYearIncomeError) this.vatDeclaration.premium_selected = false;
  }

  private recalcTotalVat(): void {
    const refund = Number(this.vatDeclaration.vat_refund_value) || 0;

    const total = Object.values(this.calculatedVat).reduce((sum, val) => sum + (val || 0), 0);

    this.totalVat = (total - refund).toFixed(2);
    this.recalcPaymentOrRefund();
  }

  public updateVat(field: string, value: number): void {
    this.calculatedVat[field] = value;
    this.recalcTotalVat();
  }

  public updateVatRefund(): void {
    this.recalcTotalVat();
  }

  public recalcPaymentOrRefund() {
    const total = Number(this.totalVat) || 0;
    const advance = Number(this.vatDeclaration.advance_payments) || 0;
    this.vatPayOrRefund = (total - advance).toFixed(2);
  }

  public handleDeclarationType(value: declarationTypes) {
    this.vatDeclaration.declaration_type = value;

    // po zmianie typu deklaracji odśwież błędy
    this.additionalIncomeService.refreshErrors();
  }

  public handleOkButton(
    value: declarationTypes = declarationTypes.VAT_INCOME,
    callback: (d: MatDialogRef<ConfirmCancelDialogComponent>) => any = () => {}
  ): void {
    if (value === declarationTypes.VAT_INCOME) {
      const dialog = this.dialog.open(ConfirmCancelDialogComponent);
      dialog.componentInstance.id = "gotoVatIncome";
      dialog.componentInstance.title = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.CONFIRM_TITLE_VAT";
      dialog.componentInstance.description = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.CONFIRM_DESCRIPTION_VAT";
      dialog.componentInstance.submitButtonTitle = "COMMON.PROCEED";
      dialog.componentInstance.submitActionEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
        dialog.disableClose = true;
        callback(dialog);
        this.handleDeclarationType(value);
        this.clearBusinessDeclaration();
      });
    } else {
      const dialog = this.dialog.open(ConfirmCancelDialogComponent);
      dialog.componentInstance.id = "gotoVatIncome";
      dialog.componentInstance.title = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.CONFIRM_TITLE_BUSINESS";
      dialog.componentInstance.description = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.CONFIRM_DESCRIPTION_BUSINESS";
      dialog.componentInstance.submitButtonTitle = "COMMON.PROCEED";
      dialog.componentInstance.submitActionEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
        dialog.disableClose = true;
        callback(dialog);
        this.handleDeclarationType(value);
        this.clearVatIncomeDeclaration();
      });
    }
    this.vatDeclaration.premium_selected = false;
  }

  public handleShowPremium() {
    const dialog = this.dialog.open(ConfirmCancelDialogComponent);
    dialog.componentInstance.id = "gotoVatIncome";
    dialog.componentInstance.title = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.BUSINESS_PREMIUM_MODAL.TITLE";
    dialog.componentInstance.description = "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.BUSINESS_PREMIUM_MODAL.DESCRIPTION";
    dialog.componentInstance.submitButtonTitle = "COMMON.PROCEED";
    dialog.componentInstance.submitActionEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      dialog.disableClose = true;
      this.vatDeclaration.premium_selected = true;
      this.additionalIncomeService.getMainStatement().setPremiumPackage();
      this.vatDeclaration.income_sum = 0;
      this.vatDeclaration.last_year_income = 0;
    });
  }

  public handleGoPremium(goPremium: boolean) {
    this.vatDeclaration.premium_selected = goPremium;
    if (goPremium) this.additionalIncomeService.getMainStatement().setPremiumPackage();
  }

  private clearBusinessDeclaration() {
    this.vatDeclaration.income_sum = null;
    this.vatDeclaration.last_year_income = null;
  }

  private clearVatIncomeDeclaration() {
    this.vatDeclaration.vat_refund_value = null;
    this.vatDeclaration.advance_payments = null;
    this.vatDeclaration.reverse_charge_third_vat_rate = null;
    this.vatDeclaration.reverse_charge_third = null;
    this.vatDeclaration.reverse_charge_second_vat_rate = null;
    this.vatDeclaration.reverse_charge_second = null;
    this.vatDeclaration.reverse_charge_first_vat_rate = null;
    this.vatDeclaration.reverse_charge_first = null;
    this.vatDeclaration.reduced_rate_net_revenues = null;
    this.vatDeclaration.zero_percent_net_revenues = null;
    this.vatDeclaration.general_rate_net_revenues = null;
    this.vatDeclaration.sales_value_settled_by_recipient = null;
    this.vatDeclaration.reverse_charge = null;
    this.vatDeclaration.type_of_taxation = null;
  }

  public handleFieldsValue(fieldName: string, value: boolean) {
    this.vatDeclaration[fieldName] = value;
  }

  public get statementYear(): number {
    return this.additionalIncomeService.getMainStatement().fullYear;
  }

  protected readonly codes = VerificationCodes;
  protected readonly declarationTypes = declarationTypes;
}
